import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { IndianRupee, ArrowRight } from 'lucide-react';
import { suggestIconAndColor } from '../utils/categorySuggestions';
import { groupCategories } from '../utils/categoryGroups';
import FormPageLayout from '../components/common/FormPageLayout';
import Alert from '../components/common/Alert';
import DateTimeInput from '../components/common/DateTimeInput';
import CalculatorInput from '../components/common/CalculatorInput';
import FileUpload from '../components/common/FileUpload';
import AttachmentViewerModal from '../components/common/AttachmentViewerModal';
import MobileSelect from '../components/common/MobileSelect';
import AddAccountModal from '../components/account/AddAccountModal';
import AddCategoryModal from '../components/category/AddCategoryModal';
import { getIconComponent } from '../components/common/IconPicker';
import { useCategoriesQuery, useAccountsQuery, useCreateTransaction, useUpdateTransaction, useTransactionQuery, useDescriptionSuggestions } from '../hooks/useQueries';
import { uploadAPI } from '../services/api';
import { formatDate, getCurrentTime } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const initialFormData = {
  dateTime: { date: formatDate(new Date(), 'input'), time: getCurrentTime() },
  description: '',
  type: 'expense',
  category: '',       // ObjectId of category
  account: '',        // ObjectId of account (was bankName)
  amount: '',
  isTDSDeducted: false,
  tdsAmount: '',
  notes: '',
  fromAccount: '',    // ObjectId for transfer source
  toAccount: '',      // ObjectId for transfer destination
  attachment: null
};

export default function TransactionFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const prefill = location.state?.prefill;
  const initialType = prefill?.type || searchParams.get('type') || 'expense';

  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch transaction if editing
  const { data: transaction } = useTransactionQuery(id, { enabled: !!id });

  const { data: categories = [] } = useCategoriesQuery();
  const { data: accounts = [] } = useAccountsQuery();
  const createTransactionMutation = useCreateTransaction();
  const updateTransactionMutation = useUpdateTransaction();

  const { descriptions: descriptionSuggestions = [], topCategories = [], topAccounts = [] } = useDescriptionSuggestions();

  const descriptionCategoryMap = useMemo(() => {
    const map = {};
    for (const item of descriptionSuggestions) {
      if (item.description) map[item.description] = { category: item.categoryId, type: item.type, accountId: item.accountId };
    }
    return map;
  }, [descriptionSuggestions]);

  const [formData, setFormData] = useState({ ...initialFormData, type: initialType });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);
  const [showDescSuggestions, setShowDescSuggestions] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);

  const [attachmentViewer, setAttachmentViewer] = useState({
    isOpen: false,
    src: null,
    mimeType: null,
    filename: null
  });

  const isEditing = !!id;
  const isTransfer = formData.type === 'transfer';

  // Filter categories by type
  const incomeCategories = categories.filter(c => c.type === 'income');
  const expenseCategories = categories.filter(c => c.type === 'expense');
  const currentCategories = formData.type === 'income' ? incomeCategories : expenseCategories;

  // Filter active accounts
  const activeAccounts = accounts.filter(a => !a.isDeleted && a.isActive !== false);

  // Convert categories to MobileSelect options with groups
  const { categoryOptions, categoryGroups } = useMemo(() => {
    const opts = [];
    const groupOrder = [];
    const usedNames = new Set();

    // 1. Most Used
    const mostUsed = topCategories
      .map(name => currentCategories.find(c => c.name === name))
      .filter(Boolean);

    if (mostUsed.length > 0) {
      groupOrder.push('Most Used');
      mostUsed.forEach(cat => {
        opts.push({
          value: cat._id, label: cat.name,
          icon: getIconComponent(cat.icon), iconColor: cat.color,
          group: 'Most Used'
        });
        usedNames.add(cat.name);
      });
    }

    // 2. Suggested based on description
    if (formData.description && formData.description.length >= 2) {
      const suggestion = suggestIconAndColor(formData.description, formData.type);
      if (suggestion) {
        const recommended = currentCategories.filter(cat => {
          if (usedNames.has(cat.name)) return false;
          const catSuggestion = suggestIconAndColor(cat.name, formData.type);
          return catSuggestion.icon === suggestion.icon;
        });
        if (recommended.length > 0) {
          groupOrder.push('Suggested');
          recommended.forEach(cat => {
            opts.push({
              value: cat._id, label: cat.name,
              icon: getIconComponent(cat.icon), iconColor: cat.color,
              group: 'Suggested'
            });
            usedNames.add(cat.name);
          });
        }
      }
    }

    // 3. Remaining — grouped by semantic category type
    const remaining = currentCategories.filter(c => !usedNames.has(c.name));
    if (remaining.length > 0) {
      const { groups, categoriesByGroup } = groupCategories(remaining);
      groups.forEach(groupName => {
        groupOrder.push(groupName);
        categoriesByGroup[groupName].forEach(cat => {
          opts.push({
            value: cat._id, label: cat.name,
            icon: getIconComponent(cat.icon), iconColor: cat.color,
            group: groupName
          });
        });
      });
    }

    return {
      categoryOptions: opts,
      categoryGroups: groupOrder.length > 0 ? groupOrder : null
    };
  }, [currentCategories, topCategories, formData.description, formData.type]);

  // Convert accounts to MobileSelect options — sorted by usage frequency
  const accountOptions = useMemo(() => {
    const sorted = [...activeAccounts].sort((a, b) => {
      const aIdx = topAccounts.indexOf(a.name);
      const bIdx = topAccounts.indexOf(b.name);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.name.localeCompare(b.name);
    });
    return sorted.map(acc => ({
      value: acc._id,
      label: acc.name,
      icon: getIconComponent(acc.icon),
      iconColor: acc.color
    }));
  }, [activeAccounts, topAccounts]);

  // Filter description suggestions — returns full objects
  const filteredDescSuggestions = useMemo(() => {
    if (!formData.description || formData.description.length < 1) return [];
    const query = formData.description.toLowerCase();
    return descriptionSuggestions
      .filter(s => s.description && s.description.toLowerCase().includes(query) && s.description.toLowerCase() !== query)
      .slice(0, 6);
  }, [formData.description, descriptionSuggestions]);

  // Load transaction data when editing
  useEffect(() => {
    if (transaction) {
      // Extract IDs from populated objects (API returns { _id, name, ... })
      const categoryId = transaction.category?._id || transaction.category || '';
      const accountId = transaction.account?._id || transaction.account || '';
      const toAccountId = transaction.toAccount?._id || transaction.toAccount || '';

      setFormData({
        dateTime: {
          date: formatDate(transaction.date, 'input'),
          time: transaction.time || '12:00'
        },
        description: transaction.description,
        type: transaction.type,
        category: categoryId,
        account: accountId,
        amount: parseFloat(transaction.amount?.toFixed(2)),
        isTDSDeducted: transaction.isTDSDeducted || false,
        tdsAmount: transaction.tdsAmount || '',
        notes: transaction.notes || '',
        fromAccount: transaction.type === 'transfer' ? accountId : '',
        toAccount: toAccountId,
        attachment: transaction.attachment || null
      });
    }
  }, [transaction]);

  // Apply prefill data from voice/image parser
  useEffect(() => {
    if (prefill && !isEditing) {
      setFormData(prev => ({
        ...prev,
        type: prefill.type || prev.type,
        amount: prefill.amount?.toString() || prev.amount,
        description: prefill.description || prev.description,
        category: prefill.category || prev.category,
        account: prefill.account || (prefill.bankName ? activeAccounts.find(a => a.name === prefill.bankName)?._id : null) || prev.account,
        fromAccount: prefill.fromAccount || prev.fromAccount,
        toAccount: prefill.toAccount || prev.toAccount,
        dateTime: {
          date: prefill.date || prev.dateTime.date,
          time: prefill.time || prev.dateTime.time
        }
      }));
    }
  }, [prefill, isEditing]);

  const handleViewAttachment = (src, mimeType, filename) => {
    setAttachmentViewer({
      isOpen: true,
      src,
      mimeType,
      filename: filename || 'attachment'
    });
  };

  const closeAttachmentViewer = () => {
    setAttachmentViewer({
      isOpen: false,
      src: null,
      mimeType: null,
      filename: null
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (name === 'type') {
      setFormData((prev) => ({
        ...prev,
        type: value,
        category: '',
        isTDSDeducted: false,
        tdsAmount: '',
        ...(value !== 'transfer' && { fromAccount: '', toAccount: '' }),
        ...(value === 'transfer' && { account: '' })
      }));
    }

    const selectedCatForTDS = categories.find(c => c._id === value);
    if (name === 'category' && selectedCatForTDS?.name !== 'Salary') {
      setFormData((prev) => ({
        ...prev,
        isTDSDeducted: false,
        tdsAmount: ''
      }));
    }

    if (name === 'isTDSDeducted' && !checked) {
      setFormData((prev) => ({
        ...prev,
        isTDSDeducted: false,
        tdsAmount: ''
      }));
    }
  };

  const handleDescriptionSelect = (suggestion) => {
    const description = typeof suggestion === 'string' ? suggestion : suggestion.description;
    setFormData(prev => {
      const updates = { ...prev, description };
      if (prev.type !== 'transfer') {
        const meta = descriptionCategoryMap[description];
        if (meta) {
          if (!prev.category && meta.category && meta.type === prev.type && currentCategories.some(c => c._id === meta.category)) {
            updates.category = meta.category;
          }
          if (!prev.account && meta.accountId && activeAccounts.some(a => a._id === meta.accountId)) {
            updates.account = meta.accountId;
          }
        }
      }
      return updates;
    });
    setShowDescSuggestions(false);
  };

  const handleAccountAdded = (newAccount) => {
    setShowAddAccountModal(false);
    if (newAccount?._id) {
      setFormData(prev => ({ ...prev, account: newAccount._id }));
    }
  };

  const handleCategoryAdded = (newCategory) => {
    setShowAddCategoryModal(false);
    if (newCategory?._id) {
      setFormData(prev => ({ ...prev, category: newCategory._id }));
    }
  };

  const validate = () => {
    const errors = {};

    if (!formData.dateTime?.date) {
      errors.dateTime = 'Date is required';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }

    if (isTransfer) {
      if (!formData.fromAccount) {
        errors.fromAccount = 'Please select source account';
      }
      if (!formData.toAccount) {
        errors.toAccount = 'Please select destination account';
      }
      if (formData.fromAccount && formData.toAccount && formData.fromAccount === formData.toAccount) {
        errors.toAccount = 'Source and destination must be different';
      }
    } else {
      if (!formData.description.trim()) {
        errors.description = 'Description is required';
      } else if (formData.description.length < 2) {
        errors.description = 'Description must be at least 2 characters';
      }

      if (!formData.category) {
        errors.category = 'Please select a category';
      }

      if (!formData.account) {
        errors.account = 'Please select an account';
      }

      if (formData.isTDSDeducted && !formData.tdsAmount) {
        errors.tdsAmount = 'TDS amount is required when TDS is deducted';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      let attachmentData = null;

      if (formData.attachment?.file instanceof File) {
        const uploadResult = await uploadAPI.attachment(formData.attachment.file);
        if (uploadResult && uploadResult.url) {
          attachmentData = {
            url: uploadResult.url,
            publicId: uploadResult.publicId,
            filename: uploadResult.filename,
            mimeType: uploadResult.mimeType
          };
        }
      } else if (formData.attachment?.url) {
        attachmentData = {
          url: formData.attachment.url,
          publicId: formData.attachment.publicId,
          filename: formData.attachment.filename,
          mimeType: formData.attachment.mimeType
        };
      }

      if (isTransfer) {
        const data = {
          type: 'transfer',
          date: formData.dateTime.date,
          time: formData.dateTime.time,
          fromAccount: formData.fromAccount,
          toAccount: formData.toAccount,
          amount: parseFloat(formData.amount),
          description: formData.description || `Transfer`,
          notes: formData.notes,
          attachment: attachmentData
        };

        if (isEditing) {
          await updateTransactionMutation.mutateAsync({ id, data });
        } else {
          await createTransactionMutation.mutateAsync(data);
        }
      } else {
        const selectedCat = categories.find(c => c._id === formData.category);
        const isSalaryCategory = selectedCat?.name === 'Salary';
        const hasTDS = isSalaryCategory && formData.isTDSDeducted && formData.tdsAmount;

        const data = {
          date: formData.dateTime.date,
          time: formData.dateTime.time,
          description: formData.description,
          type: formData.type,
          category: formData.category,
          account: formData.account,
          amount: parseFloat(formData.amount),
          currency: 'INR',
          exchangeRate: 1,
          isTDSDeducted: isSalaryCategory ? formData.isTDSDeducted : false,
          tdsAmount: hasTDS ? parseFloat(formData.tdsAmount) : 0,
          notes: formData.notes,
          attachment: attachmentData
        };

        if (isEditing) {
          await updateTransactionMutation.mutateAsync({ id, data });
        } else {
          await createTransactionMutation.mutateAsync(data);
        }
      }

      toast.success(isEditing ? 'Transaction updated' : 'Transaction added');
      navigate(-1);
    } catch (err) {
      setError(err.error || err.response?.data?.message || 'Failed to save transaction');
    } finally {
      setLoading(false);
    }
  };

  const toAccountOptions = activeAccounts.filter(a => a._id !== formData.fromAccount);

  const pageTitle = isEditing
    ? (isTransfer ? 'Edit Transfer' : 'Edit Transaction')
    : (isTransfer ? 'New Transfer' : 'Add Transaction');

  return (
    <FormPageLayout title={pageTitle}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Transaction Type - Compact segmented control */}
        <div className="grid grid-cols-3 gap-1 p-1 bg-surface-100 rounded-xl">
          <button
            type="button"
            onClick={() => handleChange({ target: { name: 'type', value: 'expense' } })}
            className={`py-2 rounded-lg font-medium text-sm transition-all ${
              formData.type === 'expense'
                ? 'bg-white text-expense-600 shadow-sm'
                : 'text-surface-500'
            }`}
          >
            Expense
          </button>
          <button
            type="button"
            onClick={() => handleChange({ target: { name: 'type', value: 'income' } })}
            className={`py-2 rounded-lg font-medium text-sm transition-all ${
              formData.type === 'income'
                ? 'bg-white text-income-600 shadow-sm'
                : 'text-surface-500'
            }`}
          >
            Income
          </button>
          <button
            type="button"
            onClick={() => handleChange({ target: { name: 'type', value: 'transfer' } })}
            className={`py-2 rounded-lg font-medium text-sm transition-all ${
              formData.type === 'transfer'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-surface-500'
            }`}
          >
            Transfer
          </button>
        </div>

        {/* Transfer-specific fields */}
        {isTransfer ? (
          <>
            {/* Amount - prominent */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Amount (₹)</label>
              <CalculatorInput
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                className="input text-lg font-semibold"
                icon={IndianRupee}
                error={!!validationErrors.amount}
                autoFocus
              />
              {validationErrors.amount && (
                <p className="text-expense-600 text-xs mt-1">{validationErrors.amount}</p>
              )}
            </div>

            {/* From → To Accounts */}
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <label className="text-xs font-medium text-surface-500 mb-1 block">From</label>
                <MobileSelect
                  value={formData.fromAccount}
                  onChange={(value) => handleChange({ target: { name: 'fromAccount', value } })}
                  options={accountOptions}
                  placeholder="Source"
                  label="From Account"
                  columns={2}
                  size="sm"
                  error={!!validationErrors.fromAccount}
                  showIconInTrigger
                  disabled={activeAccounts.length === 0}
                  actionButton={{
                    label: 'Add New',
                    onClick: () => setShowAddAccountModal(true)
                  }}
                />
                {validationErrors.fromAccount && (
                  <p className="text-expense-600 text-xs mt-1">{validationErrors.fromAccount}</p>
                )}
              </div>

              <ArrowRight className="w-4 h-4 text-surface-400 mt-5 flex-shrink-0" />

              <div className="flex-1 min-w-0">
                <label className="text-xs font-medium text-surface-500 mb-1 block">To</label>
                <MobileSelect
                  value={formData.toAccount}
                  onChange={(value) => handleChange({ target: { name: 'toAccount', value } })}
                  options={toAccountOptions.map(acc => ({
                    value: acc._id,
                    label: acc.name,
                    icon: getIconComponent(acc.icon),
                    iconColor: acc.color
                  }))}
                  placeholder="Destination"
                  label="To Account"
                  columns={2}
                  size="sm"
                  error={!!validationErrors.toAccount}
                  showIconInTrigger
                  disabled={activeAccounts.length === 0}
                />
                {validationErrors.toAccount && (
                  <p className="text-expense-600 text-xs mt-1">{validationErrors.toAccount}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Description <span className="text-surface-400 font-normal">(Optional)</span></label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                onFocus={() => setShowDescSuggestions(true)}
                onBlur={() => setTimeout(() => setShowDescSuggestions(false), 150)}
                onKeyDown={(e) => e.key === 'Escape' && setShowDescSuggestions(false)}
                placeholder="Moving funds to savings"
                enterKeyHint="next"
                autoComplete="off"
                className="input"
              />
              {showDescSuggestions && filteredDescSuggestions.length > 0 && (
                <div className="mt-1 bg-white border border-surface-200 rounded-xl shadow-lg max-h-36 overflow-y-auto">
                  {filteredDescSuggestions.map(s => (
                    <button
                      key={s.description}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleDescriptionSelect(s)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-surface-50 flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{s.description}</span>
                      {s.category && (
                        <span className="text-[10px] text-surface-400 whitespace-nowrap">{s.category}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date & Time */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Date & Time</label>
              <DateTimeInput
                name="dateTime"
                value={formData.dateTime}
                onChange={(value) => setFormData(prev => ({ ...prev, dateTime: value }))}
                error={!!validationErrors.dateTime}
              />
              {validationErrors.dateTime && (
                <p className="text-expense-600 text-xs mt-1">{validationErrors.dateTime}</p>
              )}
            </div>

            {/* Note - full row */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Note</label>
              <input
                type="text"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add note..."
                className="input text-sm"
              />
            </div>

            {/* Receipt */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Receipt</label>
              <FileUpload
                value={formData.attachment}
                onChange={(attachment) => setFormData(prev => ({ ...prev, attachment }))}
                accept="image/*,.pdf"
                label="Add file"
                maxSizeMB={2}
                onView={handleViewAttachment}
                compact
              />
            </div>
          </>
        ) : (
          <>
            {/* Amount - prominent first field */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Amount (₹)</label>
              <CalculatorInput
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                className="input text-lg font-semibold"
                icon={IndianRupee}
                error={!!validationErrors.amount}
                autoFocus
              />
              {validationErrors.amount && (
                <p className="text-expense-600 text-xs mt-1">{validationErrors.amount}</p>
              )}
            </div>

            {/* Description - before category so auto-suggestion can fill it */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                onFocus={() => setShowDescSuggestions(true)}
                onBlur={() => setTimeout(() => setShowDescSuggestions(false), 150)}
                onKeyDown={(e) => e.key === 'Escape' && setShowDescSuggestions(false)}
                placeholder="What was this for?"
                enterKeyHint="next"
                autoComplete="off"
                className={`input ${validationErrors.description ? 'input-error' : ''}`}
              />
              {showDescSuggestions && filteredDescSuggestions.length > 0 && (
                <div className="mt-1 bg-white border border-surface-200 rounded-xl shadow-lg max-h-36 overflow-y-auto">
                  {filteredDescSuggestions.map(s => (
                    <button
                      key={s.description}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleDescriptionSelect(s)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-surface-50 flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{s.description}</span>
                      {s.category && (
                        <span className="text-[10px] text-surface-400 whitespace-nowrap">{s.category}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {validationErrors.description && (
                <p className="text-expense-600 text-xs mt-1">{validationErrors.description}</p>
              )}
            </div>

            {/* Category & Account - always side by side */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-surface-500 mb-1 block">Category</label>
                <MobileSelect
                  value={formData.category}
                  onChange={(value) => handleChange({ target: { name: 'category', value } })}
                  options={categoryOptions}
                  groups={categoryGroups}
                  placeholder={currentCategories.length === 0 ? 'Add category' : 'Select'}
                  label="Select Category"
                  columns={3}
                  size="sm"
                  error={!!validationErrors.category}
                  showIconInTrigger
                  actionButton={{
                    label: 'Add New',
                    onClick: () => setShowAddCategoryModal(true)
                  }}
                />
                {validationErrors.category && (
                  <p className="text-expense-600 text-xs mt-1">{validationErrors.category}</p>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-surface-500 mb-1 block">Account</label>
                <MobileSelect
                  value={formData.account}
                  onChange={(value) => handleChange({ target: { name: 'account', value } })}
                  options={accountOptions}
                  placeholder={activeAccounts.length === 0 ? 'Add account' : 'Select'}
                  label="Select Account"
                  columns={2}
                  size="sm"
                  error={!!validationErrors.account}
                  showIconInTrigger
                  actionButton={{
                    label: 'Add New',
                    onClick: () => setShowAddAccountModal(true)
                  }}
                />
                {validationErrors.account && (
                  <p className="text-expense-600 text-xs mt-1">{validationErrors.account}</p>
                )}
              </div>
            </div>

            {/* Date & Time */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Date & Time</label>
              <DateTimeInput
                name="dateTime"
                value={formData.dateTime}
                onChange={(value) => setFormData(prev => ({ ...prev, dateTime: value }))}
                error={!!validationErrors.dateTime}
              />
              {validationErrors.dateTime && (
                <p className="text-expense-600 text-xs mt-1">{validationErrors.dateTime}</p>
              )}
            </div>

            {/* TDS Section (only for admin with income Salary category) */}
            {user?.isAdmin && formData.type === 'income' && categories.find(c => c._id === formData.category)?.name === 'Salary' && (
              <div className="p-3 bg-surface-50 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isTDSDeducted"
                    name="isTDSDeducted"
                    checked={formData.isTDSDeducted}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 rounded border-surface-300 focus:ring-primary-500"
                  />
                  <label htmlFor="isTDSDeducted" className="text-sm font-medium text-surface-700">
                    TDS Deducted at Source
                  </label>
                </div>

                {formData.isTDSDeducted && (
                  <div>
                    <label className="text-xs font-medium text-surface-500 mb-1 block">TDS Amount (₹)</label>
                    <CalculatorInput
                      name="tdsAmount"
                      value={formData.tdsAmount}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="input"
                      error={!!validationErrors.tdsAmount}
                    />
                    {validationErrors.tdsAmount && (
                      <p className="text-expense-600 text-xs mt-1">{validationErrors.tdsAmount}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Note - full row */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Note</label>
              <input
                type="text"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add note..."
                className="input text-sm"
              />
            </div>

            {/* Receipt */}
            <div>
              <label className="text-xs font-medium text-surface-500 mb-1 block">Receipt</label>
              <FileUpload
                value={formData.attachment}
                onChange={(attachment) => setFormData(prev => ({ ...prev, attachment }))}
                accept="image/*,.pdf"
                label="Add file"
                maxSizeMB={2}
                onView={handleViewAttachment}
                compact
              />
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary flex-1"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`flex-1 ${
              isTransfer
                ? 'btn-primary'
                : formData.type === 'income'
                ? 'btn-income'
                : 'btn-expense'
            }`}
            disabled={loading}
          >
            {loading
              ? 'Saving...'
              : isEditing
              ? 'Update'
              : isTransfer
              ? 'Transfer'
              : 'Add'}
          </button>
        </div>
      </form>

      {/* Attachment Viewer Modal */}
      <AttachmentViewerModal
        isOpen={attachmentViewer.isOpen}
        onClose={closeAttachmentViewer}
        src={attachmentViewer.src}
        mimeType={attachmentViewer.mimeType}
        filename={attachmentViewer.filename}
      />

      {/* Add Account Modal */}
      <AddAccountModal
        isOpen={showAddAccountModal}
        onClose={() => setShowAddAccountModal(false)}
        onSuccess={handleAccountAdded}
      />

      {/* Add Category Modal */}
      <AddCategoryModal
        isOpen={showAddCategoryModal}
        onClose={() => setShowAddCategoryModal(false)}
        onSuccess={handleCategoryAdded}
      />
    </FormPageLayout>
  );
}
